import React, { useState, useCallback, useMemo, useEffect, useLayoutEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { connect } from "react-redux";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import HelpIcon from "@mui/icons-material/Help";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  IconButton,
  useMediaQuery,
  useTheme,
  Box
} from "@mui/material";
import { menuConfig, getAccessibleMenu } from "../../config/menuConfig";
import { logout } from "../../action/auth";
import customConfig from "../../config";
import UserProfileMenu from "./UserProfileMenu";
import { useSidebarContext } from "../../context/SidebarContext";
import { getPrimaryThemeColor, getSecondaryThemeColor } from "../../utils/getThemeColor";
import "./LeftNavResponsive.css";

const INITIAL_SECTIONS = {
  devices: false,
  manufacturing: false,
  users: false,
  statistics: false,
  others: false,
  settings: false,
};

const LeftNavResponsive = ({ auth, tenantInfo, logout }) => {
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // < 900px
  const { updateSidebarState } = useSidebarContext();
  
  // Get theme class names and colors from tenant info
  const themeClass = useMemo(() => {
    const primary = tenantInfo?.primaryColor || 'dark-blue';
    const secondary = tenantInfo?.secondaryColor || 'lime';
    return `${primary}-theme ${secondary}-theme-secondary`;
  }, [tenantInfo]);
  
  const primaryColor = useMemo(() => getPrimaryThemeColor(tenantInfo), [tenantInfo]);
  const secondaryColor = useMemo(() => getSecondaryThemeColor(tenantInfo), [tenantInfo]);
  const handleLogout = useCallback(() => {
    logout();
  }, [logout]);
  const [isExpanded, setIsExpanded] = useState(false); // DEFAULT: collapsed (icon-only)
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState(INITIAL_SECTIONS);
  const [isHoverExpanded, setIsHoverExpanded] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const hoverTimeoutRef = React.useRef(null);
  const [hasLayoutMounted, setHasLayoutMounted] = useState(false);
  const prevIsMobileRef = useRef(isMobile);

  const blurActiveElement = useCallback(() => {
    if (typeof document !== "undefined" && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  }, []);

  // Single source for "sidebar is expanded" – ensures class and layout match on first paint (fixes first-load vs refresh)
  const sidebarExpanded = isExpanded || isHoverExpanded || isMobileOpen;
  // On first paint after login/navigation, always use collapsed layout so hierarchy (main vs submenu) is correct; after mount use real state
  const effectiveExpanded = hasLayoutMounted ? sidebarExpanded : false;
  // On mobile, always render drawer content as expanded so closing doesn't flash (content collapsing during slide-out)
  const effectiveExpandedForDrawer = isMobile ? true : effectiveExpanded;

  // Sync sidebar state to context for content area layout
  useLayoutEffect(() => {
    updateSidebarState(isExpanded, isHoverExpanded);
  }, [isExpanded, isHoverExpanded, updateSidebarState]);

  // Set mounted after first paint so first frame always shows collapsed layout with no subitems (fixes "vertically straight" on first load)
  useEffect(() => {
    const id = requestAnimationFrame(() => setHasLayoutMounted(true));
    return () => cancelAnimationFrame(id);
  }, []);

  // Cleanup hover timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  // Reset mobile drawer and collapse sidebar only when transitioning from mobile -> desktop
  useEffect(() => {
    const wasMobile = prevIsMobileRef.current;
    if (wasMobile && !isMobile) {
      setIsMobileOpen(false);
      setIsExpanded(false);
      setIsHoverExpanded(false);
      setExpandedSections(INITIAL_SECTIONS);
    }
    prevIsMobileRef.current = isMobile;
  }, [isMobile]);

  // Build user context for permission checks
  const userContext = useMemo(
    () => ({
      userLevel: auth?.userLevel || 0,
      roles: auth?.roles || auth?.userRoles || [],
      tenantId: tenantInfo?.tenantId || ""
    }),
    [auth, tenantInfo]
  );

  // Get accessible menu items for current user
  const accessibleMenu = useMemo(
    () => getAccessibleMenu(menuConfig, userContext),
    [userContext]
  );

  // Toggle section expansion with accordion behavior
  const toggleSection = useCallback((sectionId) => {
    setExpandedSections(prev => {
      // If opening a section, close all others (accordion behavior)
      if (!prev[sectionId]) {
        const newSections = {};
        Object.keys(prev).forEach(key => {
          newSections[key] = key === sectionId;
        });
        return newSections;
      }
      // If closing the current section
      return {
        ...prev,
        [sectionId]: false
      };
    });
  }, []);

  // Expand section on hover only (doesn't close on hover)
  const expandSectionOnHover = useCallback((sectionId) => {
    setExpandedSections(prev => {
      // Only expand if not already expanded
      if (prev[sectionId]) {
        return prev; // Don't toggle if already open
      }
      // Expand this section and close all others
      const newSections = {};
      Object.keys(prev).forEach(key => {
        newSections[key] = key === sectionId;
      });
      return newSections;
    });
  }, []);

  // Check if route is active
  const isActive = useCallback((path) => {
    if (!path) return false;
    return location.pathname.startsWith(path);
  }, [location.pathname]);

  // Check if section has active child
  const hasActiveChild = useCallback((section) => {
    if (!section.subitems) return false;
    return section.subitems.some(item => isActive(item.path));
  }, [isActive]);

  // Close mobile menu on link click - with proper delay for navigation
  const handleLinkClick = useCallback((e) => {
    blurActiveElement();
    if (isMobile) {
      // Don't close immediately - let navigation happen first
      setTimeout(() => {
        setIsMobileOpen(false);
      }, 300);
    }
  }, [isMobile, blurActiveElement]);

  // Render a menu item (subitem)
  const renderMenuItem = (item, isSubitem = true) => {
    const Icon = item.icon;
    const active = isActive(item.path);

    const collapsed = !effectiveExpandedForDrawer;
    return (
      <ListItem
        key={item.id}
        disablePadding
        sx={{
          display: collapsed && isSubitem ? 'flex' : 'block',
          justifyContent: collapsed && isSubitem ? 'flex-start' : undefined,
        }}
      >
        <ListItemButton
          className={isSubitem ? 'sidebar-subitem-button' : ''}
          component={Link}
          to={item.path}
          onClick={handleLinkClick}
          selected={active}
          sx={{
            minHeight: collapsed && isSubitem ? 32 : 40,
            justifyContent: effectiveExpandedForDrawer ? 'flex-start' : 'center',
            px: effectiveExpandedForDrawer ? (isSubitem ? 2.5 : 1) : (isSubitem ? 0 : 1.5),
            py: collapsed && isSubitem ? 0 : 1.25,
            borderRadius: 1,
            mx: collapsed && isSubitem ? 0 : 1,
            mb: collapsed && isSubitem ? 0.25 : 0.5,
            width: collapsed && isSubitem ? 32 : undefined,
            minWidth: collapsed && isSubitem ? 32 : undefined,
            backgroundColor: active ? 'rgba(255, 255, 255, 0.15)' : 'transparent',
            border: active ? '1px solid rgba(255, 255, 255, 0.3)' : '1px solid transparent',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.12)',
              borderColor: 'rgba(255, 255, 255, 0.3)',
            },
            '&.Mui-selected': {
              backgroundColor: 'rgba(255, 255, 255, 0.15)',
              fontWeight: 500,
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
              }
            }
          }}
        >
          <ListItemIcon
            className={isSubitem ? 'sidebar-subitem-icon' : 'sidebar-section-icon'}
            sx={{
              minWidth: isSubitem ? 28 : 0,
              width: effectiveExpandedForDrawer ? (isSubitem ? 28 : 'auto') : (isSubitem ? 32 : 40),
              mr: effectiveExpandedForDrawer ? 1.5 : 0,
              justifyContent: 'center',
              color: '#ffffff',
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0,
              '& svg': {
                width: isSubitem ? '22px' : '24px',
                height: isSubitem ? '22px' : '24px',
              }
            }}
          >
            {Icon && <Icon style={{ fontSize: isSubitem ? 22 : 24 }} />}
          </ListItemIcon>
          <ListItemText
            primary={item.name}
            sx={{
              opacity: effectiveExpandedForDrawer ? 1 : 0,
              visibility: effectiveExpandedForDrawer ? 'visible' : 'hidden',
              width: effectiveExpandedForDrawer ? 'auto' : 0,
              overflow: 'visible',
              flex: effectiveExpandedForDrawer ? '1 1 auto' : 'none',
              minWidth: 0,
              color: '#ffffff',
              m: 0,
              transition: 'opacity 0.2s ease 0.1s, visibility 0s ease 0.1s',
              '& .MuiListItemText-primary': {
                fontSize: isSubitem ? 14 : 15,
                fontWeight: active ? 500 : 400,
                whiteSpace: 'nowrap',
                lineHeight: 1.3,
                overflow: 'visible',
              }
            }}
          />
        </ListItemButton>
      </ListItem>
    );
  };

  // Render a section with subitems
  const renderSection = (section) => {
    const Icon = section.icon;
    // On first paint don't open any section so only main menu icons show (no "vertically straight" main+submenu)
    const isOpen = hasLayoutMounted && expandedSections[section.id];
    const hasActive = hasActiveChild(section);

    return (
      <Box
        key={section.id}
        style={!effectiveExpandedForDrawer ? { width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' } : undefined}
        sx={{
          mb: effectiveExpandedForDrawer ? 1 : 2,
          ...(!effectiveExpandedForDrawer && {
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }),
        }}
      >
        {/* Section header / toggle - when collapsed, center the main menu icon only */}
        <ListItem
          disablePadding
          style={!effectiveExpandedForDrawer ? { display: 'block', width: '100%', paddingLeft: 0, paddingRight: 0, textAlign: 'center' } : undefined}
          sx={{
            display: 'block',
            width: !effectiveExpandedForDrawer ? '100%' : undefined,
          }}
        >
          <ListItemButton
            onClick={() => {
              blurActiveElement();
              toggleSection(section.id);
            }}
            onMouseEnter={() => {
              if (!isMobile) expandSectionOnHover(section.id);
            }}
            selected={hasActive}
            style={!effectiveExpandedForDrawer ? { width: 40, minWidth: 40, maxWidth: 40, margin: '0 auto', paddingLeft: 0, paddingRight: 0, display: 'inline-flex' } : undefined}
            sx={{
              minHeight: effectiveExpandedForDrawer ? 44 : 36,
              justifyContent: effectiveExpandedForDrawer ? 'flex-start' : 'center',
              px: effectiveExpandedForDrawer ? 1 : 0,
              py: 0.75,
              borderRadius: 1,
              mx: effectiveExpandedForDrawer ? 1 : 0,
              marginLeft: !effectiveExpandedForDrawer ? 'auto' : undefined,
              marginRight: !effectiveExpandedForDrawer ? 'auto' : undefined,
              width: !effectiveExpandedForDrawer ? 40 : undefined,
              minWidth: !effectiveExpandedForDrawer ? 40 : undefined,
              flex: !effectiveExpandedForDrawer ? '0 0 40px' : undefined,
              backgroundColor: hasActive ? 'rgba(255, 255, 255, 0.08)' : 'transparent',
              border: '1px solid transparent',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.12)',
                borderColor: 'rgba(255, 255, 255, 0.2)',
              },
              '&.Mui-selected': {
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.15)',
                }
              }
            }}
          >
            <ListItemIcon
              className="sidebar-section-icon"
              sx={{
                minWidth: 0,
                mr: effectiveExpandedForDrawer ? 1.5 : 0,
                justifyContent: 'center',
                color: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                width: effectiveExpandedForDrawer ? 'auto' : '40px',
                height: '40px',
                flexShrink: 0,
                '& svg': {
                    width: '24px',
                    height: '24px',
                }
              }}
            >
                {Icon && <Icon style={{ fontSize: 24 }} />}
            </ListItemIcon>
            <ListItemText
              primary={section.name}
              sx={{
                opacity: effectiveExpandedForDrawer ? 1 : 0,
                visibility: effectiveExpandedForDrawer ? 'visible' : 'hidden',
                width: effectiveExpandedForDrawer ? 'auto' : 0,
                minWidth: effectiveExpandedForDrawer ? undefined : 0,
                flex: effectiveExpandedForDrawer ? 1 : '0 0 0',
                overflow: 'hidden',
                color: '#ffffff',
                m: 0,
                transition: 'opacity 0.2s ease 0.1s, visibility 0s ease 0.1s',
                '& .MuiListItemText-primary': {
                  fontSize: 16,
                  fontWeight: 500,
                  whiteSpace: 'nowrap',
                  lineHeight: 1.3,
                  overflow: 'visible',
                }
              }}
            />
            {section.subitems && section.subitems.length > 0 && (
              <ExpandMoreIcon
                style={{
                  color: '#ffffff',
                  fontSize: '18px',
                  transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  display: effectiveExpandedForDrawer ? 'block' : 'none',
                  flexShrink: 0,
                  marginLeft: '8px',
                }}
              />
            )}
          </ListItemButton>
        </ListItem>

        {/* Subitems with Collapse - when collapsed indent submenu (subordinates); when expanded indent for hierarchy */}
        {section.subitems && section.subitems.length > 0 && (
          <Collapse in={isOpen} timeout={{ enter: 300, exit: 200 }} unmountOnExit>
            <List
              component="div"
              disablePadding
              sx={{
                marginLeft: 0,
                marginTop: 0.5,
                paddingLeft: effectiveExpandedForDrawer ? 2 : 3, // expanded: 16px; collapsed: 24px so submenu icons sit right of main (subordinates)
                ...(!effectiveExpandedForDrawer && {
                  '& .MuiListItem-root': { display: 'flex', justifyContent: 'flex-start' },
                  '& .MuiListItemButton-root': {
                    minHeight: 32,
                    height: 32,
                    width: 32,
                    minWidth: 32,
                    maxWidth: 32,
                    marginLeft: 0,
                    marginRight: 0.25,
                    marginTop: 0.125,
                    marginBottom: 0.125,
                    paddingLeft: 0,
                    paddingRight: 0,
                    justifyContent: 'center',
                  },
                }),
              }}
            >
              {section.subitems.map(item => renderMenuItem(item, true))}
            </List>
          </Collapse>
        )}
      </Box>
    );
  };

  return (
    <>
      {/* Mobile toggle button - LEFT SIDE */}
      {isMobile && (
        <IconButton
          onClick={() => setIsMobileOpen(!isMobileOpen)}
          sx={{
            position: 'fixed !important',
            top: '16px !important',
            left: '16px !important',
            zIndex: '9999 !important',
            backgroundColor: 'rgba(255, 255, 255, 0.95) !important',
            border: 'none !important',
            color: `${primaryColor} !important`,
            borderRadius: '8px !important',
            width: '44px',
            height: '44px',
            display: 'flex !important',
            alignItems: 'center',
            justifyContent: 'center',
            '&:hover': {
              backgroundColor: '#ffffff !important',
            },
            boxShadow: '0 2px 8px rgba(0,0,0,0.2) !important',
            [theme.breakpoints.up('md')]: {
              display: 'none !important',
            }
          }}
          aria-label="Toggle menu"
        >
          {isMobileOpen ? <CloseIcon style={{ fontSize: '22px' }} /> : <MenuIcon style={{ fontSize: '22px' }} />}
        </IconButton>
      )}
      {/* MUI Drawer for Sidebar */}
      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={isMobile ? isMobileOpen : true}
        onClose={() => {
          blurActiveElement();
          setIsMobileOpen(false);
        }}
        transitionDuration={300}
        ModalProps={{
          keepMounted: true, // Better mobile performance
          slotProps: {
            backdrop: { TransitionProps: { timeout: 300 } },
          },
        }}
        className={`${themeClass} left-navbar ${effectiveExpandedForDrawer ? 'left-navbar-expanded' : ''}`}
        slotProps={{
          transition: { timeout: 300 },
          paper: {
            className: `${themeClass} left-navbar ${effectiveExpandedForDrawer ? 'left-navbar-expanded' : ''}`,
            'data-collapsed': !effectiveExpandedForDrawer ? 'true' : undefined,
            style: {
              backgroundColor: primaryColor,
              borderRight: '1px solid rgba(255, 255, 255, 0.1)',
              ...(!effectiveExpandedForDrawer && { paddingLeft: 0, paddingRight: 0 }),
            },
            sx: {
              backgroundColor: `${primaryColor} !important`,
              borderRight: '1px solid rgba(255, 255, 255, 0.1) !important',
              // Ensure all text and icons are white, but exclude avatar text
              '& *:not(.MuiAvatar-root):not(.MuiAvatar-root *)': {
                color: '#ffffff !important',
              }
            },
            onMouseEnter: () => {
              if (!isMobile) {
                if (hoverTimeoutRef.current) {
                  clearTimeout(hoverTimeoutRef.current);
                }
                setIsHoverExpanded(true);
              }
            },
            onMouseLeave: () => {
              if (!isMobile) {
                if (hoverTimeoutRef.current) {
                  clearTimeout(hoverTimeoutRef.current);
                }
                hoverTimeoutRef.current = setTimeout(() => {
                  if (!isProfileMenuOpen) {
                  setIsHoverExpanded(false);
                  }
                }, 100);
              }
            },
          },
        }}
        sx={{
          width: isMobile && !isMobileOpen ? 0 : (isMobile ? 300 : (effectiveExpandedForDrawer ? 300 : 72)),
          minWidth: isMobile && !isMobileOpen ? 0 : undefined,
          overflow: isMobile && !isMobileOpen ? 'hidden' : 'visible',
          flexShrink: 0,
          position: 'relative',
          ...(isMobile && !isMobileOpen && { backgroundColor: 'transparent', boxShadow: 'none' }),
          '& .MuiDrawer-paper': {
            width: isMobile ? 300 : (effectiveExpandedForDrawer ? 300 : 72),
            boxSizing: 'border-box',
            backgroundColor: `${primaryColor} !important`,
            borderRight: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: isMobile ? 0 : '0 16px 0 0',
            transition: hasLayoutMounted ? theme.transitions.create('width', {
              easing: theme.transitions.easing.easeInOut,
              duration: 300,
            }) : 'none',
            overflowX: 'hidden',
            overflowY: 'hidden',
            boxShadow: '2px 0 12px rgba(0, 0, 0, 0.3)',
            position: 'fixed',
            left: 0,
            top: 0,
            height: '100vh',
            maxHeight: '100vh',
            // Above MUI Modal (1300) so sidebar stays hoverable when dropdowns are open
            zIndex: isMobile ? 1300 : 1310,
            // Only pin position for permanent (desktop) drawer; let temporary drawer slide off when closed
            ...((!isMobile || isMobileOpen) && { transform: 'translateX(0)' }),
            display: 'flex',
            flexDirection: 'column',
            padding: 0,
          },
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
          }
        }}
      >
        {/* Inner wrapper with left-navbar class so CSS hierarchy applies on first paint (fixes first-load layout) */}
        <Box
          className={`left-navbar ${effectiveExpandedForDrawer ? 'left-navbar-expanded' : ''}`}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            height: '100%',
            width: '100%',
          }}
        >
        {/* Desktop toggle button */}
        {!isMobile && (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2, pb: 1.5, flexShrink: 0 }}>
            <IconButton
              onClick={() => setIsExpanded(!isExpanded)}
              sx={{
                width: 40,
                height: 40,
                backgroundColor: 'transparent',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                color: '#ffffff',
                borderRadius: 1,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderColor: '#ffffff',
                }
              }}
              title={isExpanded ? "Collapse" : "Expand"}
              aria-label="Toggle sidebar"
            >
              <MenuIcon style={{ fontSize: '20px' }} />
            </IconButton>
          </Box>
        )}

        {/* Menu container - scrollable area; when collapsed no horizontal padding so icons center */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            px: effectiveExpandedForDrawer ? (isMobile ? 0.5 : 2) : 0,
            pl: effectiveExpandedForDrawer ? undefined : 0,
            pr: effectiveExpandedForDrawer ? undefined : 0,
            pt: isMobile ? 8 : 0,
            pb: 1,
            minHeight: 0,
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            alignItems: effectiveExpandedForDrawer ? undefined : 'center',
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(255, 255, 255, 0.2)',
              borderRadius: '3px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: 'rgba(255, 255, 255, 0.4)',
            },
          }}
        >
          <List
            className={!effectiveExpandedForDrawer ? 'sidebar-main-list collapsed' : 'sidebar-main-list'}
            style={!effectiveExpandedForDrawer ? { paddingLeft: 0, paddingRight: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' } : undefined}
            sx={{
              pt: 0,
              px: 0,
              paddingLeft: 0,
              paddingRight: 0,
              ...(!effectiveExpandedForDrawer && {
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                width: '100%',
              }),
            }}
          >
            {accessibleMenu.length > 0 ? (
              accessibleMenu.map(section => renderSection(section))
            ) : (
              <Box sx={{ p: 2.5, textAlign: 'center', color: '#ffffff', fontSize: 12 }}>
                No menu items available
              </Box>
            )}
          </List>
        </Box>

        {/* Footer: single row, profile left, ? and Logout right, all vertically aligned */}
        <Box
          sx={{
            flexShrink: 0,
            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 0.75,
            px: 0.5,
            minHeight: 48,
          }}
        >
          <UserProfileMenu
            logout={handleLogout}
            username={auth?.username || auth?.email || 'User'}
            themeClass={themeClass}
            primaryColor={primaryColor}
            onSettingsNavigate={() => {
              if (isMobile) {
                blurActiveElement();
                setIsMobileOpen(false);
              }
            }}
          />
          {effectiveExpandedForDrawer && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <IconButton
                onClick={() => window.open(customConfig.supportURL, '_blank', 'noopener, noreferrer')}
                sx={{ color: '#ffffff', p: 1, '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' } }}
                aria-label="Support"
                title="Support"
              >
                <HelpIcon style={{ fontSize: 22 }} />
              </IconButton>
              <IconButton
                onClick={handleLogout}
                sx={{ color: '#ffffff', p: 1, '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' } }}
                aria-label="Logout"
                title="Logout"
              >
                <ExitToAppIcon style={{ fontSize: 22 }} />
              </IconButton>
            </Box>
          )}
        </Box>
        </Box>
      </Drawer>
    </>
  );
};

const mapStateToProps = (state) => ({
  auth: state.auth,
  tenantInfo: state.tenant?.tenantInfo || {}
});

export default connect(mapStateToProps, { logout })(LeftNavResponsive);
